import type {ReactElement} from 'react';
import React, {useMemo, useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Button, Type, Size} from '@Components/button';
import {Text, TextSize} from '@Components/text';
import type {MultipageTemplateData} from '@Libraries/editor-templates-library';
import {LazyMasonryGrid} from '@Components/lazy-masonry-grid';
import type {LazyLoadingResponse} from '@Libraries/pmw-stock-media-library';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import {updateGridItem} from '@Components/base-grid';
import {useAppDispatch} from '@/hooks';
import {TEMPLATE_GRID_ITEM_DEFAULT_WEB_WIDTH, TEMPLATES_GRID_ITEM_HEIGHT_THRESHOLD} from '@Libraries/editor-templates-library';
import {isTemplateGridHeightBelowThreshold} from '@Libraries/editor-templates-library';
import {isWebpSupported} from '@Utils/browser.util';
import styles from './multipage-template-pages-panel.module.scss';
import useWindowSize from '@Hooks/useWindowSize';

interface MultipageTemplatePagesPanelProps {
  template: MultipageTemplateData;
  onBack: () => void;
  onAddAllPages: (templateHashedId: string) => Promise<void>;
  onAddSinglePage: (templateHashedId: string, pageHashedId: string) => Promise<void>;
  posterWidth?: number;
  posterHeight?: number;
  isOpenedInMobileModal?: boolean;
}

const PADDING_AND_NAV_HEIGHT = 218;

export function MultipageTemplatePagesPanel({
  template,
  onBack,
  onAddAllPages,
  onAddSinglePage,
  posterWidth,
  posterHeight,
  isOpenedInMobileModal = false,
}: MultipageTemplatePagesPanelProps): ReactElement<any> {
  const dispatch = useAppDispatch();
  const [isAdding, setIsAdding] = useState(false);
  const gridId = useMemo(() => `multipage-template-pages-grid-${template.hashedID}`, [template.hashedID]);
  const gridSelectionGroupId = useMemo(() => gridId, [gridId]);
  const {windowWidth, windowHeight} = useWindowSize();
  const maxGridHeight = isOpenedInMobileModal ? undefined : windowHeight - PADDING_AND_NAV_HEIGHT;
  const GRID_ITEM_DEFAULT_WIDTH = isOpenedInMobileModal ? windowWidth / 2 - 20 : TEMPLATE_GRID_ITEM_DEFAULT_WEB_WIDTH;
  const gridItemHeight = posterWidth && posterHeight ? (posterHeight * GRID_ITEM_DEFAULT_WIDTH) / posterWidth : null;
  const gridItemWidth = isTemplateGridHeightBelowThreshold(gridItemHeight) ? GRID_ITEM_DEFAULT_WIDTH * 2 : GRID_ITEM_DEFAULT_WIDTH;
  const gridItemHeightValue = isTemplateGridHeightBelowThreshold(gridItemHeight) ? TEMPLATES_GRID_ITEM_HEIGHT_THRESHOLD : gridItemHeight || TEMPLATES_GRID_ITEM_HEIGHT_THRESHOLD;

  const handleAddAllPages = async (): Promise<void> => {
    setIsAdding(true);
    try {
      await onAddAllPages(template.hashedID);
    } finally {
      setIsAdding(false);
    }
  };

  const getPageGridItems = async (): Promise<LazyLoadingResponse> => {
    const previewURLs = isWebpSupported() ? template.previewURLs : template.fallbackPreviewURLs;
    const pageCount = template.pageCount ?? template.pageHashedIDs?.length;

    const gridItems: GridItemStorage[] = Array.from({length: pageCount}, (_, index) => {
      const isVideo = template.pagesIsVideo[index] ?? false;
      const thumbSrc = previewURLs[index] ?? previewURLs[0];
      const videoSrc = isVideo && template.videoPreviewURLs?.length ? (template.videoPreviewURLs[index] ?? template.videoPreviewURLs[0]) : undefined;

      const pageHashedID = template.pageHashedIDs[index];
      const data = {
        pageIndex: index,
        pageHashedID,
        isViewed: false,
        isSeen: false,
        isDataIncremented: false,
      };

      if (isVideo) {
        return {
          type: GRID_ITEM_TYPE.VIDEO,
          id: `${template.hashedID}-page-${index}`,
          thumbSrc,
          videoSrc,
          width: gridItemWidth,
          height: gridItemHeightValue,
          showDurationTag: false,
          showVideoTag: true,
          data,
        } as GridItemStorage;
      } else {
        return {
          type: GRID_ITEM_TYPE.IMAGE,
          id: `${template.hashedID}-page-${index}`,
          thumbSrc,
          width: gridItemWidth,
          height: gridItemHeightValue,
          data,
        } as GridItemStorage;
      }
    });

    return {
      response: gridItems,
      offsetsData: {
        DEFAULT_SOURCE: gridItems.length,
      },
    };
  };

  const handlePageClick = async (gridItem: GridItemStorage): Promise<void> => {
    const pageHashedID = (gridItem.data as { pageHashedID?: string } | undefined)?.pageHashedID;
    if (pageHashedID === undefined || pageHashedID === null) {
      return;
    }

    dispatch(
      updateGridItem({
        gridId: gridId,
        itemId: gridItem.id,
        changes: {
          isLoading: true,
        },
      })
    );

    try {
      await onAddSinglePage(template.hashedID, pageHashedID);
    } finally {
      dispatch(
        updateGridItem({
          gridId: gridId,
          itemId: gridItem.id,
          changes: {
            isLoading: false,
          },
        })
      );
    }
  };

  return (
    <div className="flex-v-row _full-height _overflow-hidden">
      {!isOpenedInMobileModal ? (
        <div className="flexbox flex-items-center gap-3 spacing-m-b-4 spacing-p-l-4 spacing-p-r-4 spacing-p-t-4">
          <Icon icon="icon-arrow-left" size={IconSize.SIZE_ICON_20} type={IconType.GHOST} shape={IconShape.SQUARE} className="_cursor-pointer flex-shrink-0" onClick={onBack} />
          <Text val={template.templateName} size={TextSize.SMALL} bold />
        </div>
      ) : null}

      {!isOpenedInMobileModal ? (
        <div className="flexbox flex-justify-center spacing-m-b-4 spacing-p-l-4 spacing-p-r-4">
          <Button
            type={Type.SECONDARY}
            size={Size.SMALL}
            text={window.i18next.t('pmwjs_add_all_x_pages', {pageCount: template.pageCount})}
            onClick={handleAddAllPages}
            isLoading={isAdding}
            isFullWidth
            customClasses="_full-width"
          />
        </div>
      ) : null}

      <div className={`${styles.gridContainer} flex-1 spacing-p-l-4 spacing-p-t-2 spacing-p-r-4 spacing-p-b-4`} style={{maxHeight: maxGridHeight}}>
        <LazyMasonryGrid
          key={gridId}
          gridId={gridId}
          gridSelectionGroupId={gridSelectionGroupId}
          getItems={getPageGridItems}
          customDimensions={{
            width: gridItemWidth,
            height: gridItemHeightValue,
          }}
          customOnGridItemClicked={(item) => {
            void handlePageClick(item);
          }}
          clearGridOnUnmount
          maxSelectionNumber={0}
        />
      </div>
    </div>
  );
}
